import { useEffect, useRef } from "react";

import {
  Button,
  CloseSVG,
  Combobox,
  Icon,
  LanguageSVG,
} from "@newsela/angelou";
import { objectTypes, actionNames } from "@newsela/snitch";
import { Node } from "@tiptap/pm/model";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import useHasFeature from "@/features/useHasFeature";
import useUserAttributes from "@/users/useUserAttributes";
import useTracker from "@/eventStream/useTracker";
import useTranslatorControlsContext from "@/view/utils/useTranslatorControlsContext";
import useTranslationEventTracking from "../NodeWithTranslation/utils/useTranslationEventTracking";
import {
  ILanguage,
  TLanguageChangeHandler,
  ITranslationData,
} from "./TranslateDrawer.interface";
import {
  buttonContainerStyles,
  closeButtonStyles,
  comboboxStyles,
  comboboxWrapperStyles,
  headerStyles,
  iconStyles,
  titleStyles,
} from "./TranslateDrawer.style";

interface TranslateDrawerHeaderProps {
  hasBody: boolean;
  languages: ILanguage[];
  selectedLanguage: ILanguage | undefined;
  onLanguageChange: TLanguageChangeHandler;
  onCloseButtonClick: () => void;
  state: any; // EditorState from parent
  translationData?: ITranslationData;
  node: Node;
}

const TranslateDrawerHeader = ({
  hasBody,
  languages,
  onLanguageChange,
  selectedLanguage,
  onCloseButtonClick,
  state,
  translationData,
  node,
}: TranslateDrawerHeaderProps) => {
  const defaultItem: ILanguage = { name: "Choose language", code: "" };
  const selectedLanguageFoundInLanguageList = languages?.find(
    (lang) => selectedLanguage?.code === lang.code,
  );
  const selected = selectedLanguageFoundInLanguageList || defaultItem;
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  // Get translation context to access first selection state and time tracking
  const { isFirstSelection, timeSinceLastSelection, updateLanguageSelection } =
    useTranslatorControlsContext();

  // Get tracking hooks
  const { trackAction, getEventNameForEventStream } = useTracker();
  const { userAttributes } = useUserAttributes();

  // Get translation tracking metadata with first selection state and time
  const _trackingMetadata = useTranslationEventTracking({
    state,
    node,
    ...(translationData && { translationData }),
    isFirstSelection,
    timeSinceLastSelection,
    selectedLanguage,
    previousLanguage: translationData?.source_language,
  });

  useEffect(() => {
    if (drawerRef.current) {
      drawerRef.current.focus();
    }
  }, []);

  const handleLanguageChange = (changes: {
    selectedItem?: ILanguage | null;
  }) => {
    const newLanguage = changes.selectedItem;
    const previousLanguageCode = selectedLanguage?.code;

    // Simplified logic: if we have a new language with code, update the selection
    if (newLanguage?.code) {
      updateLanguageSelection(newLanguage.code, isFirstSelection);
    }

    // Track the language change with correct data
    const newTrackingMetadata = {
      ..._trackingMetadata,
      selected_language: newLanguage?.code,
      previous_language: previousLanguageCode,
    };

    // Manually trigger tracking event
    const userRolesString = Array.isArray((userAttributes as any)?.user_roles)
      ? (userAttributes as any)?.user_roles.join(",")
      : null;

    trackAction({
      event_name: getEventNameForEventStream(
        objectTypes.generatedTextAsset,
        null,
        actionNames.select,
      ),
      event_type: "click",
      dimension_properties: {
        user_roles: userRolesString,
        content_type: "Article",
        description: "select translation language",
        metadata: newTrackingMetadata,
      },
    });

    onLanguageChange(newLanguage);
  };

  // Remove problematic aria-describedby attributes that React 18 generates
  useEffect(() => {
    const comboboxInput = document.querySelector('input[role="combobox"]');
    if (comboboxInput) {
      const ariaDescribedBy = comboboxInput.getAttribute("aria-describedby");
      if (ariaDescribedBy && ariaDescribedBy.match(/^:r\d+:$/)) {
        comboboxInput.removeAttribute("aria-describedby");
      }
    }
  }, []);

  return (
    <div css={headerStyles(hasBody, showLunaAiBranding)}>
      <span css={titleStyles} id="translate-drawer-header">
        <Icon SvgComponent={LanguageSVG} __cssFor={{ root: iconStyles }} />
        Translate to
      </span>
      <div css={comboboxWrapperStyles}>
        <Combobox
          hideLabel
          label="Translate to"
          referenceHeaderId="translate-drawer-header"
          isRequired={false}
          onSelectedItemChange={handleLanguageChange}
          selectedItem={selected}
          __cssFor={{ root: comboboxStyles }}
          items={languages}
          itemToString={(item: ILanguage | null) => item?.name || ""}
          toggleButtonProps={{
            ref: drawerRef,
            "aria-label": selected.code
              ? `translating to ${selected.name}`
              : `select a language`,
            "aria-labelledby": "translate-drawer-header",
          }}
        />
      </div>
      <div css={buttonContainerStyles}>
        <Button
          ariaProps={{ "aria-label": "close translate drawer" }}
          flavor={Button.flavors.incognito}
          onClick={onCloseButtonClick}
          __cssFor={{ root: closeButtonStyles }}
        >
          <Icon SvgComponent={CloseSVG} size={Icon.sizes.md} />
        </Button>
      </div>
    </div>
  );
};

export default TranslateDrawerHeader;
